import React from "react";
import { Redirect, Route, RouteProps } from "react-router-dom";

import * as ROUTES from "common/routes.constants";
import useAuth from "contexts/Auth/useAuth";
import isHegiasStaffEmail from "utils/isHegiasStaffEmail";
import { FullpageSpinner } from "components";

/**
 * Route guard for the internal staff dashboard.
 *
 * Deliberately separate from `ProtectedRoute`: that one gates on the
 * *organization* role (owner/admin/senior/…) through a hardcoded path table,
 * which is the wrong axis entirely for a cross-tenant staff page. Mixing the two
 * would mean an org admin at a customer inheriting a HEGIAS-internal page the
 * first time somebody extended that table carelessly.
 *
 * A non-staff visitor gets the 404 page rather than a redirect to /projects or
 * an "access denied" screen: there is no reason to tell someone who wandered
 * here that the page exists.
 *
 * This is a rendering decision only. `/v3/internal/*` re-checks every request
 * with `requireHegiasEmployee`, which reads the database.
 */
const InternalRoute = (props: RouteProps) => {
	const { authenticated, me, loading } = useAuth();

	if (!authenticated) {
		return <Redirect to={`/${ROUTES.LOGIN}`} />;
	}

	// `me` arrives one request after `authenticated` flips. Without this the
	// guard would evaluate against a null user and bounce a legitimate staff
	// member to 404 on a hard page load.
	if (loading || !me) {
		return <FullpageSpinner />;
	}

	if (!isHegiasStaffEmail(me.profile?.email)) {
		return <Redirect to={`/${ROUTES.ERROR}/404`} />;
	}

	return <Route {...props} />;
};

export default InternalRoute;
